import { ActivitySectionType, BlockType } from "../../../types";
import type { FormActivityUI, FormBlockUI } from "./utils";

/** Sections every manipulatives activity is structured into, in display order. */
const MANIPULATIVES_SECTION_ORDER: readonly ActivitySectionType[] = [
  ActivitySectionType.GUIDED_DEMONSTRATION,
  ActivitySectionType.HANDS_ON,
  ActivitySectionType.GUIDED_EXPLORATION,
];

const createEmptyBlock = (id: number): FormBlockUI => ({
  id,
  type: BlockType.TEXT,
  order_by: 0,
  text: "",
  file_id: null,
  file_id_url: null,
  materials: "",
  resourceId: null,
  resourceFileId: null,
});

const withSequentialOrder = (blocks: FormBlockUI[]): FormBlockUI[] =>
  blocks.map((block, index) => ({ ...block, order_by: index }));

/**
 * Turns a regular activity into a manipulatives one.
 *
 * The blocks the activity already had are moved as they are into the first
 * section — guided demonstration — so their materials, instructions, images and
 * resources keep their exact content and ids. Nothing is copied by hand and
 * nothing is reformatted; the remaining two sections start empty.
 */
export const toManipulativesActivity = (
  activity: FormActivityUI,
): FormActivityUI => {
  const baseId = Date.now();
  const migratedBlocks = activity.blocks.length
    ? withSequentialOrder(activity.blocks)
    : [createEmptyBlock(baseId)];

  return {
    ...activity,
    has_manipulatives: true,
    manipulative_materials_list: activity.manipulative_materials_list || "",
    blocks: [],
    sections: MANIPULATIVES_SECTION_ORDER.map((section_type, index) => ({
      section_type,
      order_by: index,
      blocks:
        section_type === ActivitySectionType.GUIDED_DEMONSTRATION
          ? migratedBlocks
          : [createEmptyBlock(baseId + index * 1000)],
    })),
  };
};

/**
 * Reverts a conversion that has not been saved yet, bringing the guided
 * demonstration blocks back as the activity's own blocks.
 */
export const toRegularActivity = (
  activity: FormActivityUI,
): FormActivityUI => {
  const guidedDemonstrationBlocks =
    activity.sections.find(
      (section) =>
        section.section_type === ActivitySectionType.GUIDED_DEMONSTRATION,
    )?.blocks ?? [];

  return {
    ...activity,
    has_manipulatives: false,
    manipulative_materials_list: "",
    sections: [],
    blocks: guidedDemonstrationBlocks.length
      ? withSequentialOrder(guidedDemonstrationBlocks)
      : [createEmptyBlock(Date.now())],
  };
};
